import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { agentSettingsTabFromPath } from '../../settingsTabs';
import type { AgentSettingsTab } from '../../types';
import { NS } from './i18n';
import './AgentSettingsSkeleton.css';

/** Always four: the skeleton cannot know which tabs this user may open. */
const TABS = [0, 1, 2, 3];
const FIELD_CARDS = [0, 1, 2];
const POLICIES = [0, 1];
const USER_ROWS = [0, 1, 2, 3];

const Bone: React.FC<{ className: string }> = ({ className }) => (
  <span className={`agent-settings-skeleton__bone ${className}`} />
);

const FieldBones: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`agent-settings-skeleton__field ${className}`.trim()}>
    <Bone className="agent-settings-skeleton__label" />
    <Bone className="agent-settings-skeleton__control" />
  </div>
);

function FieldsPanel(): React.ReactElement {
  return (
    <div className="agent-settings-skeleton__panel agent-settings-skeleton__panel--fields">
      {FIELD_CARDS.map((card) => (
        <div key={card} className="agent-settings-skeleton__box">
          <Bone className="agent-settings-skeleton__box-title" />
          <Bone className="agent-settings-skeleton__line agent-settings-skeleton__line--long" />
          <Bone className="agent-settings-skeleton__control" />
        </div>
      ))}
      <div className="agent-settings-skeleton__footer">
        <Bone className="agent-settings-skeleton__button" />
      </div>
    </div>
  );
}

function WhitelistPanel(): React.ReactElement {
  return (
    <div className="agent-settings-skeleton__panel">
      <div className="agent-settings-skeleton__box">
        <Bone className="agent-settings-skeleton__box-title" />
        <Bone className="agent-settings-skeleton__line agent-settings-skeleton__line--medium" />
        {POLICIES.map((policy) => (
          <div key={policy} className="agent-settings-skeleton__policy">
            <Bone className="agent-settings-skeleton__dot" />
            <div className="agent-settings-skeleton__policy-text">
              <Bone className="agent-settings-skeleton__line agent-settings-skeleton__line--short" />
              <Bone className="agent-settings-skeleton__line agent-settings-skeleton__line--long" />
            </div>
          </div>
        ))}
      </div>
      <div className="agent-settings-skeleton__users">
        <div className="agent-settings-skeleton__users-head">
          <div className="agent-settings-skeleton__users-title">
            <Bone className="agent-settings-skeleton__box-title" />
            <Bone className="agent-settings-skeleton__line agent-settings-skeleton__line--medium" />
          </div>
          <Bone className="agent-settings-skeleton__button" />
        </div>
        <div className="agent-settings-skeleton__list">
          <div className="agent-settings-skeleton__row agent-settings-skeleton__row--head">
            <Bone className="agent-settings-skeleton__cell" />
            <Bone className="agent-settings-skeleton__cell" />
            <Bone className="agent-settings-skeleton__cell" />
          </div>
          {USER_ROWS.map((row) => (
            <div key={row} className="agent-settings-skeleton__row">
              <Bone className="agent-settings-skeleton__cell" />
              <Bone className="agent-settings-skeleton__cell" />
              <Bone className="agent-settings-skeleton__cell" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BillingPanel(): React.ReactElement {
  return (
    <div className="agent-settings-skeleton__panel agent-settings-skeleton__panel--billing">
      <div className="agent-settings-skeleton__box agent-settings-skeleton__plan">
        <div className="agent-settings-skeleton__plan-heading">
          <Bone className="agent-settings-skeleton__box-title" />
          <Bone className="agent-settings-skeleton__line agent-settings-skeleton__line--long" />
        </div>
        <FieldBones />
        <div className="agent-settings-skeleton__pair">
          <FieldBones />
          <FieldBones />
        </div>
        <div className="agent-settings-skeleton__pair">
          <FieldBones />
          <FieldBones />
        </div>
        <FieldBones />
      </div>
      <div className="agent-settings-skeleton__actions">
        <Bone className="agent-settings-skeleton__button" />
      </div>
    </div>
  );
}

const PanelBones: React.FC<{ kind: AgentSettingsTab }> = ({ kind }) => {
  if (kind === 'billing') return <BillingPanel />;
  if (kind === 'whitelist') return <WhitelistPanel />;
  return <FieldsPanel />;
};

export interface AgentSettingsPanelSkeletonProps {
  kind: AgentSettingsTab;
}

/** One tab's loading shape, for a tab that loads inside the page. */
export const AgentSettingsPanelSkeleton: React.FC<AgentSettingsPanelSkeletonProps> = ({ kind }) => {
  const { t } = useTranslation(NS);
  const text = t('loading');

  return (
    <div
      className="agent-settings-skeleton"
      role="status"
      data-testid="agent-settings-panel-skeleton"
      data-kind={kind}
    >
      <span className="agent-settings-skeleton__sr-only">{text}</span>
      <div aria-hidden="true">
        <PanelBones kind={kind} />
      </div>
    </div>
  );
};

export interface AgentSettingsSkeletonProps {
  /** Adds the agent screens' gutter, for RouteSkeleton, which draws it straight in the app chrome. */
  standalone?: boolean;
}

/**
 * Loading shape of the agent's Settings: breadcrumb, then the card with the title,
 * the tabs, the open tab's definition line and that tab's panel. No `aria-busy`:
 * on a live region it holds the announcement back.
 */
export const AgentSettingsSkeleton: React.FC<AgentSettingsSkeletonProps> = ({
  standalone = false,
}) => {
  const { t } = useTranslation(NS);
  const { pathname } = useLocation();
  const kind = agentSettingsTabFromPath(pathname);
  const text = t('loading');

  return (
    <div
      className={`agent-settings-skeleton${standalone ? ' agent-settings-skeleton--standalone' : ''}`}
      role="status"
      data-testid="agent-settings-skeleton"
      data-kind={kind}
    >
      <span className="agent-settings-skeleton__sr-only">{text}</span>
      <div className="agent-settings-skeleton__header" aria-hidden="true">
        <Bone className="agent-settings-skeleton__crumb" />
      </div>
      <div className="agent-settings-skeleton__card" aria-hidden="true">
        <Bone className="agent-settings-skeleton__title" />
        <div className="agent-settings-skeleton__nav">
          <div className="agent-settings-skeleton__tabs">
            {TABS.map((tab) => (
              <Bone key={tab} className="agent-settings-skeleton__tab" />
            ))}
          </div>
          <Bone className="agent-settings-skeleton__line agent-settings-skeleton__line--definition" />
        </div>
        <PanelBones kind={kind} />
      </div>
    </div>
  );
};
